import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, useTheme, alpha, Paper, Tabs, Tab, CircularProgress } from '@mui/material';
import {
  PersonAdd as PersonAddIcon,
  Event as EventIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Assignment as AssignmentIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Share as ShareIcon,
  Schedule as ScheduleIcon,
  Done as DoneIcon,
  AttachMoney as AttachMoneyIcon,
  Handshake as HandshakeIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../../services/api';

dayjs.extend(relativeTime);

// Map ticker types to icons and colors
const getTickerTypeConfig = (type) => {
  const configs = {
    'New Lead Contacted': {
      icon: PhoneIcon,
      color: '#2563eb',
      bgGradient: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `New lead contacted: ${leadName} by ${agentName}`
          : `New lead contacted: ${leadName}`,
    },
    'Lead Contacted': {
      icon: PhoneIcon,
      color: '#2563eb',
      bgGradient: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1) 0%, rgba(37, 99, 235, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Lead contacted: ${leadName} by ${agentName}`
          : `Lead contacted: ${leadName}`,
    },
    'Details Shared': {
      icon: ShareIcon,
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Details shared for ${leadName} by ${agentName}`
          : `Details shared for ${leadName}`,
    },
    'Visit Scheduled': {
      icon: ScheduleIcon,
      color: '#0891b2',
      bgGradient: 'linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(8, 145, 178, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Visit scheduled for ${leadName} by ${agentName}`
          : `Visit scheduled for ${leadName}`,
    },
    'Visit Confirmed': {
      icon: EventIcon,
      color: '#0891b2',
      bgGradient: 'linear-gradient(135deg, rgba(8, 145, 178, 0.1) 0%, rgba(8, 145, 178, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Visit confirmed for ${leadName} by ${agentName}`
          : `Visit confirmed for ${leadName}`,
    },
    'Visit Completed': {
      icon: DoneIcon,
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Visit completed for ${leadName} by ${agentName}`
          : `Visit completed for ${leadName}`,
    },
    'Followup completed': {
      icon: AssignmentIcon,
      color: '#8b5cf6',
      bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Follow-up completed for ${leadName} by ${agentName}`
          : `Follow-up completed for ${leadName}`,
    },
    'Lead marked as Hot Lead': {
      icon: StarIcon,
      color: '#ef4444',
      bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Lead ${leadName} marked as Hot Lead by ${agentName}`
          : `Lead ${leadName} marked as Hot Lead`,
    },
    'Price Quoted': {
      icon: AttachMoneyIcon,
      color: '#f59e0b',
      bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Price quoted for ${leadName} by ${agentName}`
          : `Price quoted for ${leadName}`,
    },
    'Offer Made': {
      icon: HandshakeIcon,
      color: '#8b5cf6',
      bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(139, 92, 246, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Offer made to ${leadName} by ${agentName}`
          : `Offer made to ${leadName}`,
    },
    'Offer Accepted': {
      icon: ThumbUpIcon,
      color: '#10b981',
      bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(16, 185, 129, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Offer accepted by ${leadName} - handled by ${agentName}`
          : `Offer accepted by ${leadName}`,
    },
    'Offer Rejected': {
      icon: ThumbDownIcon,
      color: '#ef4444',
      bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)',
      textTemplate: (leadName, agentName) => 
        agentName 
          ? `Offer rejected by ${leadName} - handled by ${agentName}`
          : `Offer rejected by ${leadName}`,
    },
  };

  return configs[type] || {
    icon: CheckCircleIcon,
    color: '#6b7280',
    bgGradient: 'linear-gradient(135deg, rgba(107, 114, 128, 0.1) 0%, rgba(107, 114, 128, 0.05) 100%)',
    textTemplate: (leadName, agentName) => type,
  };
};

// Transform API data to ticker format
const transformTickerData = (apiData) => {
  if (!apiData || !apiData.ticker_data) return [];
  
  return apiData.ticker_data.map((item, index) => {
    const config = getTickerTypeConfig(item.type);
    const timestamp = dayjs(item.timestamp);
    const leadName = item.lead_name || 'Unknown';
    const agentName = item.agent_name || null;
    
    return {
      id: item.lead_id + '_' + index,
      lead_id: item.lead_id,
      leadName,
      agentName,
      type: item.type,
      fullText: config.textTemplate(leadName, agentName),
      time: timestamp,
      icon: config.icon,
      color: config.color,
      bgGradient: config.bgGradient,
    };
  });
};

const ActivityTicker = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0); // 0: Qualified, 1: Negotiation
  const [tickerData, setTickerData] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pipelineValue, setPipelineValue] = useState(null);
  const scrollContainerRef = useRef(null);
  const autoPlayIntervalRef = useRef(null);
  const isRefreshingRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const tabDataCacheRef = useRef({});

  // Tab configuration
  const tabs = [
    { label: 'Qualified', stage: 'qualified', showPipeline: true },
    { label: 'Negotiation', stage: 'negotiation', showPipeline: true }
  ];

  // Fetch ticker data from API by stage
  const fetchTickerData = async (preserveIndex = false, isTabChange = false) => {
    try {
      isRefreshingRef.current = true;
      const currentStage = tabs[activeTab].stage;
      
      // Only show full loading state on initial load, not on tab changes
      if (!isTabChange && isInitialLoadRef.current) {
        setLoading(true);
      } else if (isTabChange) {
        // Show subtle loading indicator for tab changes
        setIsRefreshing(true);
      }
      setError(null);
      
      // Check cache first for tab changes - show cached data immediately
      if (isTabChange && tabDataCacheRef.current[currentStage]) {
        const cachedData = tabDataCacheRef.current[currentStage];
        setTickerData(cachedData.data);
        setPipelineValue(cachedData.pipelineValue);
        setCurrentIndex(0);
        setLoading(false);
        // Continue to fetch fresh data in background
      }
      
      const response = await api.post('/report/ticker/by-stage/', {
        stage: currentStage
      });
      
      const transformedData = transformTickerData(response.data);
      
      // Set pipeline value if available
      const newPipelineValue = response.data.pipeline_value !== undefined 
        ? response.data.pipeline_value 
        : null;
      
      // Cache the data for this tab
      tabDataCacheRef.current[currentStage] = {
        data: transformedData,
        pipelineValue: newPipelineValue
      };
      
      // Preserve current index if data length allows, otherwise reset to 0
      if (transformedData.length > 0) {
        if (preserveIndex && tickerData.length > 0 && currentIndex < transformedData.length) {
          // Keep current index if within bounds - don't trigger scroll
          // Use functional update to avoid triggering scroll effect
          setCurrentIndex(prev => prev);
        } else {
          setCurrentIndex(0);
        }
      } else {
        setCurrentIndex(0);
      }
      
      setTickerData(transformedData);
      setPipelineValue(newPipelineValue);
      
      // Small delay to ensure state is updated before allowing scroll
      setTimeout(() => {
        isRefreshingRef.current = false;
        setIsRefreshing(false);
      }, 100);
      
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } catch (err) {
      console.error('Error fetching ticker data:', err);
      // Log more details for debugging
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
        setError(`Failed to load ticker data: ${err.response.status} ${err.response.data?.error || err.response.statusText}`);
      } else if (err.request) {
        console.error('Request made but no response:', err.request);
        setError('Failed to load ticker data: No response from server');
      } else {
        console.error('Error setting up request:', err.message);
        setError(`Failed to load ticker data: ${err.message}`);
      }
      // Set empty array on error to prevent crashes
      setTickerData([]);
      setPipelineValue(null);
      isRefreshingRef.current = false;
      setIsRefreshing(false);
      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
      }
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and refresh interval - refetch when active tab changes
  useEffect(() => {
    const isTabChange = !isInitialLoadRef.current;
    fetchTickerData(false, isTabChange); // Don't preserve index on initial load or tab change
    
    // Refresh every 30 seconds (preserve index to avoid scrolling)
    const interval = setInterval(() => {
      fetchTickerData(true, false); // Preserve index on refresh
    }, 30000);

    return () => clearInterval(interval);
  }, [activeTab]); // Re-fetch when tab changes

  // Auto-scroll through items
  useEffect(() => {
    if (!isAutoPlay || isPaused) return;

    autoPlayIntervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % tickerData.length);
    }, 5000); // Change item every 5 seconds

    return () => {
      if (autoPlayIntervalRef.current) {
        clearInterval(autoPlayIntervalRef.current);
      }
    };
  }, [tickerData.length, isAutoPlay, isPaused]);

  // Scroll to current item (only scroll within ticker container, not page)
  useEffect(() => {
    // Don't scroll if we're currently refreshing data (to prevent page scroll on refresh)
    if (isRefreshingRef.current) {
      return;
    }
    
    if (scrollContainerRef.current && tickerData.length > 0) {
      const itemElement = scrollContainerRef.current.children[currentIndex];
      if (itemElement && scrollContainerRef.current) {
        // Use scrollLeft to scroll within container only, preventing page scroll
        const container = scrollContainerRef.current;
        const itemLeft = itemElement.offsetLeft;
        const itemWidth = itemElement.offsetWidth;
        const containerWidth = container.offsetWidth;
        
        // Calculate the scroll position to center the item
        const scrollLeft = itemLeft - (containerWidth / 2) + (itemWidth / 2);
        
        // Smooth scroll within container only (doesn't affect page scroll)
        container.scrollTo({
          left: scrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }, [currentIndex, tickerData.length]);

  const handlePrevious = () => {
    setIsAutoPlay(false);
    setCurrentIndex((prev) => (prev - 1 + tickerData.length) % tickerData.length);
  };

  const handleNext = () => {
    setIsAutoPlay(false);
    setCurrentIndex((prev) => (prev + 1) % tickerData.length);
  };

  const handleItemClick = (index, leadId) => {
    setIsAutoPlay(false);
    setCurrentIndex(index);
    // Navigate to lead details page
    if (leadId) {
      navigate(`/leads/${leadId}`);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Reset to beginning on manual refresh
    await fetchTickerData(false);
    
    // Wait for state to update, then clear refresh flag and scroll to beginning
    setTimeout(() => {
      // Clear refresh flag to allow scroll
      isRefreshingRef.current = false;
      
      // Set index to 0 to scroll to beginning
      setCurrentIndex(0);
      setIsAutoPlay(true);
      
      // Manually scroll to beginning after a brief delay to ensure DOM is updated
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        }
      }, 50);
    }, 100);
    
    // Reset refreshing state after animation
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setCurrentIndex(0);
    setIsAutoPlay(true);
  };

  // Format currency helper - converts to Lakhs/Crores format
  const formatCurrency = (value) => {
    if (!value || value === 0) return '₹0';
    
    const crore = 10000000; // 1 crore = 1,00,00,000
    const lakh = 100000; // 1 lakh = 1,00,000
    
    if (value >= crore) {
      const crores = value / crore;
      return `₹${crores.toFixed(2)} Cr`;
    } else if (value >= lakh) {
      const lakhs = value / lakh;
      return `₹${lakhs.toFixed(2)} Lacs`;
    } else {
      // For values less than 1 lakh, show full amount
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
      }).format(value);
    }
  };

  // Show loading or empty state
  if (loading && tickerData.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
          py: 1,
          px: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
            py: 2,
          }}
        >
          <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            Loading ticker data...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (error || tickerData.length === 0) {
    const currentStage = tabs[activeTab].label;
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: alpha(theme.palette.background.default, 0.4),
        }}
      >
        {/* Tabs Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            px: 1.5,
            pt: 0.5,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                py: 0.75,
                px: 2,
                fontSize: '0.8125rem',
                fontWeight: 500,
                textTransform: 'none',
              },
            }}
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={tab.stage} 
                label={tab.label}
                sx={{
                  color: activeTab === index ? 'primary.main' : 'text.secondary',
                }}
              />
            ))}
          </Tabs>
        </Box>

        {/* Empty State Message */}
        <Box sx={{ py: 2, px: 1.5 }}>
          <Typography variant="body2" color="text.secondary" align="center">
            {error || `No activities for leads in "${currentStage}" stage`}
          </Typography>
        </Box>
      </Paper>
    );
  }

  const currentItem = tickerData[currentIndex];
  const IconComponent = currentItem?.icon || CheckCircleIcon;

  // Helper function to render text with highlighted lead name
  const renderHighlightedText = (text, leadName, color) => {
    if (!leadName || !text) return text;
    
    const parts = text.split(leadName);
    return (
      <>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {index < parts.length - 1 && (
              <Typography
                component="span"
                sx={{
                  color: color,
                  fontWeight: 700,
                  bgcolor: alpha(color, 0.15),
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  mx: 0.25,
                  display: 'inline-block',
                }}
              >
                {leadName}
              </Typography>
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: 1,
        overflow: 'hidden',
        border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        bgcolor: alpha(theme.palette.background.default, 0.4),
      }}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Tabs Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
          px: 1.5,
          pt: 0.5,
          position: 'relative',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              py: 0.75,
              px: 2,
              fontSize: '0.8125rem',
              fontWeight: 500,
              textTransform: 'none',
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab 
              key={tab.stage} 
              label={tab.label}
              sx={{
                color: activeTab === index ? 'primary.main' : 'text.secondary',
              }}
            />
          ))}
        </Tabs>
        
        {/* Subtle loading indicator for tab changes */}
        {isRefreshing && !isInitialLoadRef.current && (
          <Box
            sx={{
              position: 'absolute',
              right: tabs[activeTab].showPipeline ? 180 : 20,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <CircularProgress size={16} sx={{ color: 'text.secondary' }} />
          </Box>
        )}
        
        {/* Pipeline Value Display */}
        {tabs[activeTab].showPipeline && pipelineValue !== null && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.6875rem',
                fontWeight: 500,
              }}
            >
              Pipeline Value:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontSize: '0.8125rem',
                fontWeight: 700,
              }}
            >
              {formatCurrency(pipelineValue)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Ticker Content */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 1,
          px: 1.5,
          position: 'relative',
        }}
      >
        {/* Left Navigation Arrow */}
        <IconButton
          size="small"
          onClick={handlePrevious}
          sx={{
            bgcolor: 'transparent',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: alpha(theme.palette.action.hover, 0.5),
              color: 'text.primary',
            },
            flexShrink: 0,
            width: 28,
            height: 28,
            p: 0.5,
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Scrollable Container */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            py: 0.25,
          }}
        >
          {tickerData.map((item, index) => {
            const ItemIcon = item.icon;
            const isActive = index === currentIndex;

            return (
              <Box
                key={item.id}
                onClick={() => handleItemClick(index, item.lead_id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  bgcolor: isActive
                    ? alpha(item.color, 0.06)
                    : 'transparent',
                  border: isActive
                    ? `1px solid ${alpha(item.color, 0.2)}`
                    : '1px solid transparent',
                  scrollSnapAlign: 'center',
                  flexShrink: 0,
                  minWidth: 'fit-content',
                  '&:hover': {
                    bgcolor: alpha(item.color, 0.08),
                    border: `1px solid ${alpha(item.color, 0.2)}`,
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: alpha(item.color, 0.08),
                    color: item.color,
                    flexShrink: 0,
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ItemIcon sx={{ fontSize: 16 }} />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.125,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 400,
                      fontSize: '0.8125rem',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.3,
                    }}
                  >
                    {renderHighlightedText(item.fullText, item.leadName, item.color)}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '0.6875rem',
                      whiteSpace: 'nowrap',
                      fontWeight: 400,
                    }}
                  >
                    {item.time.fromNow()}
                  </Typography>
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Right Navigation Arrow */}
        <IconButton
          size="small"
          onClick={handleNext}
          sx={{
            bgcolor: 'transparent',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: alpha(theme.palette.action.hover, 0.5),
              color: 'text.primary',
            },
            flexShrink: 0,
            width: 28,
            height: 28,
            p: 0.5,
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Refresh Button */}
        <IconButton
          size="small"
          onClick={handleRefresh}
          disabled={isRefreshing}
          sx={{
            bgcolor: 'transparent',
            color: 'text.secondary',
            '&:hover': {
              bgcolor: alpha(theme.palette.action.hover, 0.5),
              color: 'text.primary',
            },
            '&:disabled': {
              opacity: 0.5,
            },
            flexShrink: 0,
            width: 28,
            height: 28,
            p: 0.5,
            ml: 0.25,
            transition: 'transform 0.3s ease',
            transform: isRefreshing ? 'rotate(360deg)' : 'rotate(0deg)',
          }}
          title="Refresh ticker"
        >
          <RefreshIcon 
            sx={{ 
              fontSize: 16,
              animation: isRefreshing ? 'spin 0.5s linear' : 'none',
              '@keyframes spin': {
                from: { transform: 'rotate(0deg)' },
                to: { transform: 'rotate(360deg)' },
              },
            }}
          />
        </IconButton>

        {/* Progress Indicator */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 2,
            bgcolor: alpha(theme.palette.divider, 0.5),
            overflow: 'hidden',
          }}
        >
          {isAutoPlay && !isPaused && (
            <Box
              sx={{
                height: '100%',
                bgcolor: theme.palette.primary.main,
                width: '100%',
                animation: 'progress 5s linear infinite',
                '@keyframes progress': {
                  from: {
                    transform: 'translateX(-100%)',
                  },
                  to: {
                    transform: 'translateX(0%)',
                  },
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ActivityTicker;

